import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { useOutletContext } from 'react-router-dom';
import WatchlistTable, { type WatchlistSortField } from './WatchlistTable';
import AddTickerForm from './AddTickerForm';
import { WatchlistItem } from '@catalyst-iq/shared';
import { api } from '../../services/api';

type LayoutCounts = {
  all: number;
  catalyst: number;
  momentum: number;
  none: number;
  gainers: number;
  losers: number;
  watchlist: number;
};

function getItemLabel(item: WatchlistItem): string {
  return String((item as any).label || (item.quote as any)?.label || 'none').toLowerCase();
}

function getRelVolume(item: WatchlistItem): number {
  const quote = item.quote as any;
  if (!quote) return -Infinity;
  return quote.rel_volume ?? quote.rel_vol ?? -Infinity;
}

type WatchlistFilter = 'all' | 'gaining' | 'losing' | 'flat';

const FILTER_CONFIG: Record<WatchlistFilter, { label: string; activeClass: string; activeBadge: string; dot: string | null }> = {
  all:     { label: 'All',     activeClass: 'text-emerald-700 dark:text-[#10d98e]', activeBadge: 'bg-[#10d98e]/15 text-[#10d98e]',           dot: null },
  gaining: { label: 'Gaining', activeClass: 'text-emerald-700 dark:text-[#10d98e]', activeBadge: 'bg-[#10d98e]/15 text-[#10d98e]',           dot: 'bg-[#10d98e]' },
  losing:  { label: 'Losing',  activeClass: 'text-red-600 dark:text-[#f5476a]',     activeBadge: 'bg-[#f5476a]/15 text-[#f5476a]',           dot: 'bg-[#f5476a]' },
  flat:    { label: '0%',      activeClass: 'text-gray-600 dark:text-[#c2d0e0]',    activeBadge: 'bg-gray-200 dark:bg-[#1d2d47] text-[#c2d0e0]', dot: null },
};

function getChangePct(item: WatchlistItem): number | null {
  const change = item.quote?.change_pct;
  if (change === null || change === undefined) return null;
  return change;
}

export default function WatchlistPage() {
  const { setCounts: setGlobalCounts } = useOutletContext<{
    setCounts: React.Dispatch<React.SetStateAction<LayoutCounts>>;
  }>();
  const [watchlist, setWatchlist] = useState<WatchlistItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [sortField, setSortField] = useState<WatchlistSortField>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [activeFilter, setActiveFilter] = useState<WatchlistFilter>('all');
  const [isBackgroundRefreshing, setIsBackgroundRefreshing] = useState<boolean>(false);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [refreshInterval, setRefreshInterval] = useState<number>(30000);

  // Sidebar badge = rows currently in the watchlist table
  useEffect(() => {
    setGlobalCounts((prev) => ({ ...prev, watchlist: watchlist.length }));
  }, [watchlist.length, setGlobalCounts]);
  
  const mergeEnrichedItems = (prev: WatchlistItem[], enriched: WatchlistItem[]): WatchlistItem[] => {
    const byId = new Map(enriched.map((item) => [item.id, item]));
    return prev.map((item) => {
      const next = byId.get(item.id);
      if (!next) return item;
      return {
        ...item,
        quote: next.quote,
        chart_data: next.chart_data ?? item.chart_data,
        hasActiveAlert: next.hasActiveAlert ?? item.hasActiveAlert,
        industry: next.industry ?? item.industry,
        note: next.note ?? item.note,
      };
    });
  };

  const fetchWatchlist = async (isInitial = false) => {
    if (isInitial) { setLoading(true); setError(null); }
    else { setIsBackgroundRefreshing(true); }
    try {
      // 1) Light path — paint tickers immediately
      const data = await api.getWatchlist();
      let items = data.items || [];

      if (items.length === 0) {
        items = [
          {
            id: 'seed-apple',
            ticker: 'AAPL',
            note: 'Tech giant - core holding',
            created_at: new Date(Date.now() - 400000).toISOString(),
            industry: 'Technology',
            quote: { 
              ticker: 'AAPL',price: 182.52,change_pct: 1.24,volume: 52000000,rel_volume: 1.1,updated_at: new Date().toISOString() 
            }
           },
          {
            id: 'seed-nvidia',
            ticker: 'NVDA',
            note: 'AI leader - watch volatility',
            created_at: new Date(Date.now() - 300000).toISOString(),
            industry: 'Semiconductors',
            quote: { 
              ticker: 'NVDA',price: 875.12,change_pct: 4.52,volume: 45000000,rel_volume: 2.3, updated_at: new Date().toISOString() 
            }
          },
          {
            id: 'seed-tesla',
            ticker: 'TSLA',
            note: 'EV play - support test at $160',
            created_at: new Date(Date.now() - 200000).toISOString(),
            industry: 'Automotive',
            quote: {
              ticker: 'TSLA', price: 171.05, change_pct: -2.11, volume: 82000000, rel_volume: 0.9, updated_at: new Date().toISOString()
            }
          },
          {
            id: 'seed-microsoft',
            ticker: 'MSFT',
            note: 'Cloud & OpenAI growth',
            created_at: new Date(Date.now() - 100000).toISOString(),
            industry: 'Technology',
            quote: { 
              ticker: 'MSFT',price: 421.90,change_pct: 0.45,volume: 23000000,rel_volume: 1.0,updated_at: new Date().toISOString() 
            }
          },
          {
            id: 'seed-bitcoin',
            ticker: 'IBIT',
            note: 'Bitcoin ETF for crypto exposure',
            created_at: new Date(Date.now()).toISOString(),
            industry: 'Financials',
            quote: { 
              ticker: 'IBIT',price: 38.40,change_pct: -0.85,volume: 12000000,rel_volume: 1.5,updated_at: new Date().toISOString() 
            }
          }
        ];
      }

      setWatchlist(items);
      setError(null);
      setLoading(false);

      const isSeed = items.length > 0 && items[0].id.startsWith('seed-');

      // 2) Heavy path — fill quotes / sparklines without blocking first paint
      if (!isSeed) {
        try {
          const enriched = await api.getWatchlistEnrich();
          setWatchlist((prev) => mergeEnrichedItems(prev, enriched.items || []));
        } catch (enrichErr) {
          console.warn('[Watchlist] Enrich failed; showing tickers without live quotes', enrichErr);
        }
      }

      setRefreshInterval(30000);
      const now = new Date();
      setLastUpdated(now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }));
    } catch (err: any) {
      console.warn("[Auto-Refresh] Failed to sync in background", err);
      setRefreshInterval(prev => Math.min(prev * 2, 300000));
      if (watchlist && watchlist.length === 0) {
        setError(err.message || "Couldn't load your watchlist");
      }
    } finally {
      setLoading(false);
      setIsBackgroundRefreshing(false);
    }
  };

  useEffect(() => {
    fetchWatchlist(true);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchWatchlist(false);
    }, refreshInterval);
    return () => clearTimeout(timer);
  }, [refreshInterval, watchlist]);

  const handleDelete = async (id: string) => {
    const prev = [...watchlist];

    const item = watchlist.find(i => i.id === id);

    setWatchlist(watchlist.filter(i => i.id !== id));

    try {
      await api.removeFromWatchlist(id);
      toast.success(`Removed ${item?.ticker ?? 'ticker'} from watchlist`);
    } catch {
      setWatchlist(prev);
      toast.error('Failed to remove ticker.');
    }
  };

  const handleUpdateNote = async (id: string, newNote: string) => {
    const prev = [...watchlist];

    setWatchlist(watchlist.map(i =>
      i.id === id ? { ...i, note: newNote } : i
    ));

    try {
      const updated = await api.updateWatchlistItem(id, { note: newNote });

      setWatchlist(p =>
        p.map(i => i.id === id ? { ...i, note: updated.note } : i)
      );

      toast.success('Note updated successfully.');
    } catch (err: any) {
      setWatchlist(prev);
      toast.error('Failed to update note.');
    }
  };

  const handleAddTicker = async (ticker: string, note: string) => {
    const normalized = ticker.trim().toUpperCase();

    if (watchlist.some(i => i.ticker === normalized)) {
      const err = new Error(`${normalized} is already in your watchlist.`) as Error & { code?: string };
      err.code = 'DUPLICATE';
      throw err;
    }

    const prev = [...watchlist];

    const temp: WatchlistItem = {
      id: 'temp-' + Date.now(),
      ticker: normalized,
      note: note || null,
      created_at: new Date().toISOString(),
      quote: null,
    };

    setWatchlist([temp, ...watchlist]);

    try {
      const newItem: WatchlistItem = await api.addToWatchlist({
        ticker: normalized,
        note,
      });

      setWatchlist(p =>
        p.map(i => i.id === temp.id ? newItem : i)
      );
    } catch (err: any) {
      setWatchlist(prev);
      throw err;
    }
  };
  const counts = React.useMemo(() => {
    let gaining = 0;
    let losing = 0;
    let flat = 0;
    for (const item of watchlist) {
      const change = getChangePct(item);
      if (change === null) continue;
      if (change > 0) gaining += 1;
      else if (change < 0) losing += 1;
      else flat += 1;
    }
    return { all: watchlist.length, gaining, losing, flat, total: watchlist.length };
  }, [watchlist]);

  const sortedWatchlist = React.useMemo(() => {
    const filtered = watchlist.filter((item) => {
      const change = getChangePct(item);
      if (activeFilter === 'gaining') return change !== null && change > 0;
      if (activeFilter === 'losing') return change !== null && change < 0;
      if (activeFilter === 'flat') return change !== null && change === 0;
      return true;
    });

    return [...filtered].sort((a, b) => {
      const dir = sortOrder === 'asc' ? 1 : -1;

      switch (sortField) {
        case 'ticker':
          return dir * a.ticker.localeCompare(b.ticker);
        case 'industry': {
          const indA = (a.industry && a.industry !== 'Unknown' ? a.industry : '') || '';
          const indB = (b.industry && b.industry !== 'Unknown' ? b.industry : '') || '';
          return dir * indA.localeCompare(indB);
        }
        case 'price': {
          const valA = a.quote?.price ?? -Infinity;
          const valB = b.quote?.price ?? -Infinity;
          return dir * (valA - valB);
        }
        case 'today':
        case 'change': {
          const valA = a.quote?.change_pct ?? -Infinity;
          const valB = b.quote?.change_pct ?? -Infinity;
          return dir * (valA - valB);
        }
        case 'rel_volume': {
          return dir * (getRelVolume(a) - getRelVolume(b));
        }
        case 'volume': {
          const valA = a.quote?.volume ?? -Infinity;
          const valB = b.quote?.volume ?? -Infinity;
          return dir * (valA - valB);
        }
        case 'label':
          return dir * getItemLabel(a).localeCompare(getItemLabel(b));
        case 'note': {
          const noteA = (a.note || '').toLowerCase();
          const noteB = (b.note || '').toLowerCase();
          return dir * noteA.localeCompare(noteB);
        }
        case 'created_at':
        default: {
          const timeA = new Date(a.created_at).getTime();
          const timeB = new Date(b.created_at).getTime();
          // Default: newest first when asc (matches previous behavior)
          return sortOrder === 'asc' ? timeB - timeA : timeA - timeB;
        }
      }
    });
  }, [watchlist, sortField, sortOrder, activeFilter]);

  const handleSort = (field: WatchlistSortField) => {
    if (sortField === field) {
      setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  return (
    <div className="space-y-6">

      {/* ── Hero banner ── */}
      <div className="relative rounded-2xl overflow-hidden border border-emerald-100 dark:border-[#1d2d47] bg-gradient-to-br from-emerald-50 via-teal-50/60 to-white dark:from-[#0e1624] dark:via-[#091a18]/50 dark:to-[#080c14] p-6">
        {/* Orbs */}
        <div className="pointer-events-none absolute -top-8 -right-8 w-48 h-48 rounded-full bg-gradient-to-br from-[#10d98e] to-[#06b6d4] opacity-[0.08] dark:opacity-[0.12] blur-3xl" />
        <div className="pointer-events-none absolute bottom-0 left-1/3 w-32 h-32 rounded-full bg-[#4f8ff8] opacity-[0.05] dark:opacity-[0.08] blur-2xl" />

        <div className="relative flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-[22px] font-bold tracking-tight bg-gradient-to-r from-[#10d98e] via-[#06b6d4] to-[#4f8ff8] bg-clip-text text-transparent">
              Watchlist
            </h1>
            <p className="text-[13px] text-gray-500 dark:text-[#a3b6cc] mt-0.5">
              Tracked tickers with live quotes · updates every 30s
            </p>
            <div className="text-[11px] font-mono mt-1 h-4 text-gray-400 dark:text-[#a3b6cc]">
              {isBackgroundRefreshing ? (
                <span className="text-blue-400 animate-pulse">Updating quotes...</span>
              ) : lastUpdated ? (
                <span>Last sync: {lastUpdated}</span>
              ) : null}
            </div>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white dark:bg-[#0e1624] border border-emerald-200 dark:border-[#10d98e]/30 shadow-sm">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500 dark:bg-[#10d98e]" />
            </span>
            <span className="text-[11px] font-semibold text-emerald-700 dark:text-[#10d98e] font-mono tracking-wide">{watchlist.length} TRACKED</span>
          </div>
        </div>

        {/* Stat chips — display only */}
        <div className="relative flex items-center gap-2.5 mt-4 flex-wrap">
          <div className="flex items-center gap-3 px-4 py-2 rounded-xl bg-white/80 dark:bg-[#0e1624]/80 border border-gray-100 dark:border-[#1d2d47] shadow-sm">
            <div>
              <div className="text-[18px] font-bold font-mono tabular-nums text-gray-900 dark:text-[#f1f5fb] leading-none">{counts.total}</div>
              <div className="text-[10px] text-gray-400 dark:text-[#a3b6cc] mt-0.5 uppercase tracking-wide font-semibold">Total</div>
            </div>
          </div>
          <div className="flex items-center gap-3 px-4 py-2 rounded-xl bg-[#10d98e]/8 dark:bg-[#10d98e]/10 border border-[#10d98e]/20">
            <span className="w-2 h-2 rounded-full bg-[#10d98e] shrink-0" />
            <div>
              <div className="text-[18px] font-bold font-mono tabular-nums text-[#10d98e] leading-none">{counts.gaining}</div>
              <div className="text-[10px] text-[#10d98e]/70 mt-0.5 uppercase tracking-wide font-semibold">Gaining</div>
            </div>
          </div>
          <div className="flex items-center gap-3 px-4 py-2 rounded-xl bg-[#f5476a]/8 dark:bg-[#f5476a]/10 border border-[#f5476a]/20">
            <span className="w-2 h-2 rounded-full bg-[#f5476a] shrink-0" />
            <div>
              <div className="text-[18px] font-bold font-mono tabular-nums text-[#f5476a] leading-none">{counts.losing}</div>
              <div className="text-[10px] text-[#f5476a]/70 mt-0.5 uppercase tracking-wide font-semibold">Losing</div>
            </div>
          </div>
          <div className="flex items-center gap-3 px-4 py-2 rounded-xl bg-gray-100/80 dark:bg-[#1d2d47]/30 border border-gray-200 dark:border-[#1d2d47]">
            <span className="w-2 h-2 rounded-full bg-gray-400 dark:bg-[#a3b6cc] shrink-0" />
            <div>
              <div className="text-[18px] font-bold font-mono tabular-nums text-gray-600 dark:text-[#c2d0e0] leading-none">{counts.flat}</div>
              <div className="text-[10px] text-gray-400 dark:text-[#a3b6cc] mt-0.5 uppercase tracking-wide font-semibold">0%</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filter tabs — same pattern as Alerts */}
      <div className="flex items-center gap-1 bg-gray-100/80 dark:bg-[#080c14]/60 border border-gray-200 dark:border-[#1d2d47] rounded-xl p-1 w-fit overflow-x-auto no-scrollbar">
        {(['all', 'gaining', 'losing', 'flat'] as WatchlistFilter[]).map((tab) => {
          const isActive = activeFilter === tab;
          return (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveFilter(tab)}
              className={`px-3 py-1 rounded-lg text-[11px] font-medium transition-all duration-150 whitespace-nowrap flex items-center gap-1.5 ${
                isActive
                  ? `bg-white dark:bg-[#141e30] shadow-sm ${FILTER_CONFIG[tab].activeClass}`
                  : 'text-gray-500 dark:text-[#c2d0e0] hover:text-gray-800 dark:hover:text-[#d5deea]'
              }`}
            >
              {FILTER_CONFIG[tab].dot && (
                <span className={`w-1.5 h-1.5 rounded-full ${FILTER_CONFIG[tab].dot} shrink-0`} />
              )}
              {FILTER_CONFIG[tab].label}
              <span className={`px-1.5 py-0 rounded text-[9px] font-mono ${
                isActive
                  ? FILTER_CONFIG[tab].activeBadge
                  : 'bg-gray-200/80 dark:bg-[#141822] text-gray-500 dark:text-[#c2d0e0]'
              }`}>
                {counts[tab]}
              </span>
            </button>
          );
        })}
      </div>

      {/* Error banner */}
      {error && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-xl border border-red-200 dark:border-[#f5476a]/25 bg-red-50 dark:bg-[#f5476a]/5 text-[13px] text-red-600 dark:text-[#f5476a]">
          <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
          </svg>
          {error}
        </div>
      )}

      {/* ── Main grid ── */}
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_300px] gap-6 items-start">
        <div className="min-w-0">
          {loading ? (
            <div className="rounded-2xl border border-gray-200 dark:border-[#1d2d47] bg-white dark:bg-[#0e1624] py-24 flex flex-col items-center justify-center gap-3 shadow-sm">
              <div className="w-6 h-6 border-2 border-gray-200 dark:border-[#263757] border-t-[#10d98e] rounded-full animate-spin" />
              <span className="text-[13px] text-gray-400 dark:text-[#a3b6cc]">Loading watchlist…</span>
            </div>
          ) : watchlist.length === 0 ? (
            <div className="rounded-2xl border border-gray-200 dark:border-[#1d2d47] bg-white dark:bg-[#0e1624] py-14 flex flex-col items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-50 dark:bg-[#10d98e]/10 flex items-center justify-center">
                <svg className="w-5 h-5 text-emerald-400 dark:text-[#10d98e]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5m-7.5 0l-1 3m8.5-3l1 3M12 11.25v1.5M12 9v.75" />
                </svg>
              </div>
              <p className="text-[14px] font-medium text-gray-500 dark:text-[#c2d0e0]">Your watchlist is empty</p>
              <p className="text-[12px] text-gray-400 dark:text-[#a3b6cc]">Add tickers using the form →</p>
            </div>
          ) : sortedWatchlist.length === 0 ? (
            <div className="rounded-2xl border border-gray-200 dark:border-[#1d2d47] bg-white dark:bg-[#0e1624] py-14 flex flex-col items-center gap-3">
              <p className="text-[14px] font-medium text-gray-500 dark:text-[#c2d0e0]">No tickers match this filter</p>
              <button
                type="button"
                onClick={() => setActiveFilter('all')}
                className="text-[12px] font-semibold text-[#4f8ff8] hover:underline"
              >
                Show all
              </button>
            </div>
          ) : (
            <WatchlistTable
              watchlist={sortedWatchlist}
              onDelete={handleDelete}
              onUpdateNote={handleUpdateNote}
              sortField={sortField}
              sortOrder={sortOrder}
              onSort={handleSort}
            />
          )}
        </div>
        <aside className="w-full lg:sticky lg:top-[96px] lg:max-h-[calc(100vh-112px)] lg:overflow-y-auto lg:overscroll-contain pr-0.5 [scrollbar-width:thin]">
          <AddTickerForm onAdd={handleAddTicker} />
        </aside>
      </div>
    </div>
  );
}